import { useState, useEffect } from "react";
import axios from 'axios';
import { useHistory, useParams } from "react-router-dom";
import { 
    Box, 
    Alert, 
    TextField,
    Button, 
    Paper,
    Typography,
    LinearProgress,
    IconButton,
    Grid,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Skeleton
} from "@mui/material";
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import SaveIcon from '@mui/icons-material/Save';
import ImageIcon from '@mui/icons-material/Image';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TranslateIcon from '@mui/icons-material/Translate';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const EditArticle = () => {
    const { id } = useParams();
    const [isLoading, setIsLoading] = useState(false);
    const [isFetching, setIsFetching] = useState(true);
    const [title, setTitle] = useState("");
    const [titleEn, setTitleEn] = useState("");
    const [body, setBody] = useState("");
    const [bodyEn, setBodyEn] = useState("");
    const [selectedFile, setSelectedFile] = useState(null);
    const [currentImage, setCurrentImage] = useState("");
    const [imageAlt, setImageAlt] = useState("");
    const [imageTitle, setImageTitle] = useState("");
    const [imageCaption, setImageCaption] = useState("");
    const [imageCredit, setImageCredit] = useState("");
    const [imageSource, setImageSource] = useState("");
    const [imageSourceUrl, setImageSourceUrl] = useState("");
    const [errorMessage, setErrorMessage] = useState("");
    const [successMessage, setSuccessMessage] = useState("");
    const [previewUrl, setPreviewUrl] = useState(null);
    
    let history = useHistory();

    // Charger l'article au montage du composant
    useEffect(() => {
        if (id) {
            fetchArticle();
        } else {
            setErrorMessage("ID d'article manquant");
            setIsFetching(false);
        }
    }, [id]);

    const fetchArticle = async () => {
        setIsFetching(true);
        const token = localStorage.getItem("token");
        
        try {
            const response = await axios.get(`articles/${id}`, {
                headers: {
                    Authorization: "Bearer " + token 
                }
            });
            
            // Gérer différentes structures de réponse
            const article = response.data.data || response.data;
            
            if (!article) {
                throw new Error("Article introuvable");
            }
            
            setTitle(article.title || "");
            setTitleEn(article.title_en || "");
            setBody(article.body || "");
            setBodyEn(article.body_en || "");
            setCurrentImage(article.image_source_url || "");
            setImageAlt(article.image_alt || "");
            setImageTitle(article.image_title || "");
            setImageCaption(article.image_caption || "");
            setImageCredit(article.image_credit || "");
            setImageSource(article.image_source || "");
            setImageSourceUrl(article.image_source_url || "");
            
        } catch (error) {
            console.error("Erreur de chargement:", error);
            setErrorMessage(error.response?.data?.message || "Impossible de charger l'article");
        } finally {
            setIsFetching(false);
        }
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        setSelectedFile(file);
        
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setPreviewUrl(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const removeImage = () => {
        setSelectedFile(null);
        setPreviewUrl(null);
    };

    const removeCurrentImage = () => {
        setCurrentImage("");
    };
    
    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setErrorMessage("");
        setSuccessMessage("");
       
        const token = localStorage.getItem("token");
        const data = new FormData();

        data.append("title", title);
        if (titleEn) data.append("title_en", titleEn);
        data.append("body", body);
        if (bodyEn) data.append("body_en", bodyEn);
        if (selectedFile) data.append("image", selectedFile);
        if (imageAlt) data.append("image_alt", imageAlt);
        if (imageTitle) data.append("image_title", imageTitle);
        if (imageCaption) data.append("image_caption", imageCaption);
        if (imageCredit) data.append("image_credit", imageCredit);
        if (imageSource) data.append("image_source", imageSource);
        if (imageSourceUrl) data.append("image_source_url", imageSourceUrl);

        // Pour les méthodes PUT avec FormData, utiliser _method
        data.append("_method", "PUT");

        try {
            const response = await axios.post(`articles/${id}`, data, {
                headers: {
                    Authorization: "Bearer " + token,
                    'Content-Type': 'multipart/form-data'
                }
            });
            
            setSuccessMessage("Article mis à jour avec succès !");
            setTimeout(() => {
                history.push("/admin-article");
            }, 1500);
            
        } catch (error) {
            setErrorMessage(error.response?.data?.message || "Une erreur est survenue");
            console.error(error);
        } finally {
            setIsLoading(false);
        }
    };

    if (isFetching) {
        return (
            <Box sx={{ width: "90%", margin: "auto", mt: 4, mb: 4 }}>
                <Paper elevation={2} sx={{ p: 4, borderRadius: 2 }}>
                    <Skeleton variant="text" width={300} height={50} />
                    <Skeleton variant="rectangular" height={60} sx={{ mt: 3 }} />
                    <Skeleton variant="rectangular" height={300} sx={{ mt: 2 }} />
                    <Skeleton variant="rectangular" height={60} sx={{ mt: 2 }} />
                </Paper>
            </Box>
        );
    }

    if (errorMessage && !title) {
        return (
            <Box sx={{ width: "90%", margin: "auto", mt: 4, mb: 4 }}>
                <Paper elevation={2} sx={{ p: 4, borderRadius: 2 }}>
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {errorMessage}
                    </Alert>
                    <Button
                        variant="contained"
                        startIcon={<ArrowBackIcon />}
                        onClick={() => history.push("/admin-article")}
                        sx={{ backgroundColor: '#00a652' }}
                    >
                        Retour à la liste
                    </Button>
                </Paper>
            </Box>
        );
    }

    return (
        <Box sx={{ width: "90%", margin: "auto", mt: 4, mb: 4 }}>
            {isLoading && <LinearProgress color="success" />}
            
            <Paper elevation={2} sx={{ p: 4, borderRadius: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
                    <IconButton 
                        onClick={() => history.push("/admin-article")}
                        sx={{ color: '#00a652' }}
                    >
                        <ArrowBackIcon />
                    </IconButton>
                    <Typography 
                        variant="h5" 
                        sx={{ 
                            color: '#00a652',
                            fontWeight: 500,
                            borderBottom: '2px solid #00a652',
                            paddingBottom: 1,
                            display: 'inline-block'
                        }}
                    >
                        Modifier l'article
                    </Typography>
                </Box>

                {successMessage && (
                    <Alert 
                        severity="success"
                        sx={{ mb: 3 }}
                        action={
                            <IconButton
                                color="inherit"
                                size="small"
                                onClick={() => setSuccessMessage("")}
                            >
                                <CloseIcon fontSize="inherit" />
                            </IconButton>
                        }
                    >
                        {successMessage}
                    </Alert>
                )}

                {errorMessage && (
                    <Alert 
                        severity="error"
                        sx={{ mb: 3 }}
                        action={
                            <IconButton
                                color="inherit"
                                size="small"
                                onClick={() => setErrorMessage("")}
                            >
                                <CloseIcon fontSize="inherit" />
                            </IconButton>
                        }
                    >
                        {errorMessage}
                    </Alert>
                )}

                <form onSubmit={handleSubmit}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        {/* Titre principal */}
                        <TextField
                            label="Titre *"
                            value={title}
                            required
                            onChange={(e) => setTitle(e.target.value)}
                            fullWidth
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    '&:hover fieldset': { borderColor: '#00a652' },
                                    '&.Mui-focused fieldset': { borderColor: '#00a652' },
                                }
                            }}
                        />

                        {/* Contenu principal */}
                        <Box sx={{ minHeight: '320px' }}>
                            <Typography variant="subtitle1" sx={{ mb: 1, color: 'text.secondary' }}>
                                Contenu *
                            </Typography>
                            <ReactQuill
                                theme="snow"
                                value={body}
                                onChange={setBody}
                                style={{ height: '250px', marginBottom: '20px' }}
                            />
                        </Box>

                        {/* Version anglaise (Accordéon) */}
                        <Accordion>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <TranslateIcon color="primary" />
                                    <Typography>Version anglaise (optionnel)</Typography>
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                                    <TextField
                                        label="Titre (EN)"
                                        value={titleEn}
                                        onChange={(e) => setTitleEn(e.target.value)}
                                        fullWidth
                                    />
                                    <Box sx={{ minHeight: '320px' }}>
                                        <Typography variant="subtitle1" sx={{ mb: 1, color: 'text.secondary' }}>
                                            Contenu (EN)
                                        </Typography>
                                        <ReactQuill
                                            theme="snow"
                                            value={bodyEn}
                                            onChange={setBodyEn}
                                            style={{ height: '250px' }}
                                        />
                                    </Box>
                                </Box>
                            </AccordionDetails>
                        </Accordion>

                        {/* Image actuelle */}
                        {currentImage && !previewUrl && (
                            <Box sx={{ 
                                border: '2px solid #00a652',
                                borderRadius: 1,
                                p: 2,
                                backgroundColor: 'rgba(0,166,82,0.04)',
                            }}>
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                    Image actuelle
                                </Typography>
                                <Box sx={{ position: 'relative', display: 'inline-block' }}>
                                    <img 
                                        src={currentImage} 
                                        alt="Current" 
                                        style={{ maxWidth: '100%', maxHeight: '300px', borderRadius: '8px' }}
                                    />
                                    <IconButton
                                        onClick={removeCurrentImage}
                                        sx={{
                                            position: 'absolute',
                                            top: 8,
                                            right: 8,
                                            bgcolor: 'error.main',
                                            color: 'white',
                                            '&:hover': { bgcolor: 'error.dark' }
                                        }}
                                        size="small"
                                    >
                                        <CloseIcon />
                                    </IconButton>
                                </Box>
                            </Box>
                        )}

                        {/* Nouvelle image */}
                        <Box sx={{ 
                            border: '2px dashed #00a652',
                            borderRadius: 1,
                            p: 3,
                            backgroundColor: 'rgba(0,166,82,0.04)',
                            textAlign: 'center'
                        }}>
                            <Typography variant="subtitle2" sx={{ mb: 2 }}>
                                {previewUrl ? "Nouvelle image" : "Changer l'image"}
                            </Typography>
                            {previewUrl ? (
                                <Box>
                                    <Box sx={{ position: 'relative', display: 'inline-block' }}>
                                        <img 
                                            src={previewUrl} 
                                            alt="Preview" 
                                            style={{ maxWidth: '100%', maxHeight: '300px', borderRadius: '8px' }}
                                        />
                                        <IconButton
                                            onClick={removeImage}
                                            sx={{
                                                position: 'absolute',
                                                top: 8,
                                                right: 8,
                                                bgcolor: 'error.main',
                                                color: 'white',
                                                '&:hover': { bgcolor: 'error.dark' }
                                            }}
                                            size="small"
                                        >
                                            <CloseIcon />
                                        </IconButton>
                                    </Box>
                                    <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
                                        {selectedFile?.name}
                                    </Typography>
                                </Box>
                            ) : (
                                <>
                                    <input
                                        accept="image/*"
                                        style={{ display: 'none' }}
                                        id="image-upload"
                                        type="file"
                                        onChange={handleFileChange}
                                    />
                                    <label htmlFor="image-upload">
                                        <Button
                                            component="span"
                                            startIcon={<ImageIcon />}
                                            sx={{
                                                color: '#00a652',
                                                borderColor: '#00a652',
                                                '&:hover': { backgroundColor: 'rgba(0,166,82,0.08)' }
                                            }}
                                            variant="outlined"
                                        >
                                            Sélectionner une nouvelle image
                                        </Button>
                                    </label>
                                </>
                            )}
                        </Box>

                        {/* Métadonnées de l'image (Accordéon) */}
                        <Accordion>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography>Métadonnées de l'image (optionnel)</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            label="Texte alternatif (Alt)"
                                            value={imageAlt}
                                            onChange={(e) => setImageAlt(e.target.value)}
                                            fullWidth
                                            placeholder="Description pour l'accessibilité"
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            label="Titre de l'image"
                                            value={imageTitle}
                                            onChange={(e) => setImageTitle(e.target.value)}
                                            fullWidth
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField
                                            label="Légende"
                                            value={imageCaption}
                                            onChange={(e) => setImageCaption(e.target.value)}
                                            fullWidth
                                            multiline
                                            rows={2}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <TextField
                                            label="Crédit photo"
                                            value={imageCredit}
                                            onChange={(e) => setImageCredit(e.target.value)}
                                            fullWidth
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <TextField
                                            label="Source"
                                            value={imageSource}
                                            onChange={(e) => setImageSource(e.target.value)}
                                            fullWidth
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <TextField
                                            label="URL de la source"
                                            value={imageSourceUrl}
                                            onChange={(e) => setImageSourceUrl(e.target.value)}
                                            fullWidth
                                            type="url"
                                        />
                                    </Grid>
                                </Grid>
                            </AccordionDetails>
                        </Accordion>

                        {/* Boutons de soumission */}
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
                            <Button
                                variant="outlined"
                                onClick={() => history.push("/admin-article")}
                                disabled={isLoading}
                            >
                                Annuler
                            </Button>
                            <Button
                                type="submit"
                                variant="contained"
                                startIcon={<SaveIcon />}
                                disabled={isLoading}
                                sx={{
                                    backgroundColor: '#00a652',
                                    '&:hover': { backgroundColor: '#008c46' },
                                    px: 4
                                }}
                            >
                                {isLoading ? "Mise à jour..." : "Mettre à jour"}
                            </Button>
                        </Box>
                    </Box>
                </form>
            </Paper>
        </Box>
    );
};

export default EditArticle;